/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  AvatarGroup,
  Box,
  HStack,
  Heading,
  Link,
  Spinner,
  Stack,
  Text,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from '@mybridge/ui';
import { Divider } from '@mybridge/ui/divider';
import { forwardRef, useContext, useRef, useState } from 'react';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { MyTestimonialsUserCard } from 'v4/snippets/profile/testimonials/user-card';
import { PublicProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { MyNetworkContext } from 'v4/components/network/context';
import { HandShakeSimpleIcon, SimpleCloseIcon, ChatOutLined } from '@mybridge/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';
import { Flex, IconButton, VStack } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { getRoomIdFromLS } from 'v4/utils/utils';

export const MyProfileTestimonial = forwardRef(({ ...props }, ref) => {
  const {
    isFriend,
    handshakeUser,
    isHandshakeRequested,
    handshakeLoading,
    handleHandshakeSendRevoke,
  } = useContext(MyNetworkContext);
  const {
    testimonials,
    endorseTestimonial,
    testimonialEndorsmentLoading,
    selectedTestimonial,
    testimonialEndorsmentResult,
    setSelectedTestimonial,
    publicProfileFetching,
    userProfileInfo
  } = useContext(PublicProfileContext);
  const { t } = useTranslator();
  const { id } = userProfileInfo ?? {};

  const [hoveredTestimonialId, setHoveredTestimonialId] = useState(null);
  const [loadingButtonId, setLoadingButtonId] = useState(null);
  const timerRef = useRef(null);
  const cardRefs = useRef({});
  const router = useRouter();

  const getPopupPosition = (testimonialId) => {
    const cardElement = cardRefs.current[testimonialId];
    if (!cardElement) return {};
    
    const rect = cardElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceAbove = rect.top;
    
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 480;
    
    if (isMobileViewport) {
      if (spaceAbove >= 400) {
        return {
          bottom: viewportHeight - rect.top + 8,
          left: '16px',
          right: '16px',
          width: 'calc(100vw - 32px)'
        };
      }
      return {
        top: rect.bottom + 8,
        left: '16px',
        right: '16px',
        width: 'calc(100vw - 32px)'
      };
    }
    
    let leftPos = rect.left;
    if (leftPos + 400 > viewportWidth - 16) {
      leftPos = viewportWidth - 400 - 16;
    }
    if (leftPos < 16) {
      leftPos = 16;
    }

    if (spaceAbove >= 400) {
      return {
        bottom: viewportHeight - rect.top + 8,
        left: leftPos
      };
    }
    
    return {
      top: rect.bottom + 8,
      left: leftPos
    };
  };

  const handleMouseEnter = (test) => {
    if (test?.agreed_user?.length > 0) {
      clearTimeout(timerRef.current);
      setHoveredTestimonialId(test.id);
    }
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setHoveredTestimonialId(null);
    }, 1000);
  };

  const handleHandshake = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handshakeUser?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };
  return (
    <>
      <Stack>
        <HStack alignItems="flex-start">
          <Stack flex={1}>
            <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">{t('profileTestimonials.testimonials') || "Testimonials"}</Heading>
          </Stack>
        </HStack>
        {testimonials?.length ? (
          <>
            <Stack pt={4} spacing={6}>
              {testimonials
                ?.filter?.((_, ind) => ind < 3)
                ?.map?.((ms, msIndex) => 
                 {
                  const likedBycurrentUser = ms?.agreed_user?.some(
                    (item) => item.id === id
                  );
                  const isAgreed =
                    ms?.id === testimonialEndorsmentResult?.id
                      ? testimonialEndorsmentResult?.endorsement_status === "AGREED"
                      : likedBycurrentUser;
                  return (<>
                  <MyTestimonialsUserCard
                    key={msIndex}
                    message={ms?.testimonial}
                    user={ms?.giver}
                    createdAt={ms?.created_at}
                  >
                    <HStack pl={12}>
                      {(
                        <ForceSignInWrapper>
                          <Box>
                            {ms?.id === selectedTestimonial?.id &&
                            (testimonialEndorsmentLoading ||
                              publicProfileFetching) ? (
                              <Spinner size="xs" />
                            ) : (
                              <Tooltip
                                zIndex="99999999"
                                label={
                                  !likedBycurrentUser
                                  ? `${t('publicProfile.agreeWith') || "Agree with"} ${ms?.giver?.first_name} ${t('publicProfile.onThis') || "on this?"}`
                                  : `${t('publicProfile.withdrawAgreement') || "Withdraw your agreement with"} ${ms?.giver?.first_name} ${t('publicProfile.onThisStatement') || "on this"}`}
                              >
                                <Box
                                  as={Link}
                                  onClick={(e) => {
                                    setSelectedTestimonial(ms);
                                    endorseTestimonial?.(ms);
                                  }}
                                >
                                  <HandShakeSimpleIcon
                                    width={20}
                                    height={20}
                                    color={!isAgreed ? "rgb(148, 176, 150)" : '#037F29'}
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                          {ms?.agreed_user_count <= 0 ? (
                            <>
                              <Text
                                as={Link}
                                _hover={{ textDecoration: 'underline' }}
                                fontSize="xs"
                                onClick={(e) => {
                                  setSelectedTestimonial(ms);
                                  endorseTestimonial?.(ms);
                                }}
                              >
                                {t('publicProfile.agreeWith') || "Agree with"} {ms?.giver?.first_name} {t('publicProfile.onThis') || "on this?"}
                              </Text>
                            </>
                          ) : (
                            ''
                          )}
                        </ForceSignInWrapper>
                      )}           
                      {ms?.agreed_user_count ? (
                        <>
                          <HStack
                            ref={(el) => (cardRefs.current[ms.id] = el)}
                            onMouseEnter={() => handleMouseEnter(ms)}
                            onMouseLeave={handleMouseLeave}
                            cursor="pointer"
                          >
                            <AvatarGroup>
                              {ms?.agreed_user
                                ?.filter?.((_, ind) => ind < 3)
                                ?.map?.((u, uind) => (
                                  <Avatar
                                    key={uind}
                                    name={getUserFullName(u)}
                                    src={getUserProfilePic(u)}
                                    size="xs"
                                    as={Link}
                                    href={'/p/' + u?.slug}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ))}
                            </AvatarGroup>
                            <Text fontSize="xs">
                              {ms?.agreed_user?.length > 3
                                ? `+${ms.agreed_user?.length - 3} `
                                : ''}{' '}
                              {t('profileTestimonials.agreed') || "Agreed"}
                            </Text>
                          </HStack>

                          {hoveredTestimonialId === ms.id && (
                            <Box
                              position="fixed"
                              w={['calc(100vw - 32px)', '400px']}
                              bg="white"
                              boxShadow="lg"
                              borderRadius="8px"
                              p={4}
                              pt={0}
                              zIndex={10}
                              style={getPopupPosition(ms.id)}
                              onMouseEnter={() => clearTimeout(timerRef.current)}
                              onMouseLeave={handleMouseLeave}
                            >
                              <Box position="relative" border="transparent">
                                <Divider mb={4} border="transparent"/>
                                <Box>
                                  <Flex justifyContent="space-between" alignItems="center" mb={4}>
                                    <Text fontWeight="400" fontSize="20px" color="#3D5A80" display="flex" justifyContent="center" width="100%">
                                      {t('profileSkills.agreedUsers') || "Agreed Users"}
                                    </Text>
                                    <IconButton
                                      display={"flex"}
                                      icon={<SimpleCloseIcon height="14px" width="14px" />}
                                      aria-label="Close"
                                      variant={'unstyled'}
                                      onClick={() => setHoveredTestimonialId(null)}
                                    />
                                  </Flex>
                                  <Box maxHeight="300px" overflowY="auto" p={2}>
                                    <VStack spacing={4} align="stretch">
                                      {ms?.agreed_user?.map((user, index) => (
                                        <Flex key={index} alignItems="center" justifyContent="space-between">
                                          <HStack spacing={3} cursor="pointer" onClick={() => userProfileInfo?.id === user?.id ? router.push(`/profile/me`) : router.push(`/p/${user?.slug}`)}>
                                            <Avatar src={user?.profile_pic} name={getUserFullName(user)} />
                                            <Text fontWeight="500" color="#5B5B5B" fontSize="15px">{getUserFullName(user)}</Text>
                                          </HStack>
                                          <HStack spacing={2}>
                                            <ForceSignInWrapper>
                                              {userProfileInfo?.id !== user?.id && (
                                                !isFriend(user) ? (
                                                  <HStack>
                                                    {isHandshakeRequested?.(user) ? (
                                                      /* <Tooltip label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}> */
                                                      <Tooltip zIndex="99999999" label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}>
                                                        <IconButton
                                                          onClick={() => handleHandshakeSendRevoke?.(user)}
                                                          _hover={{ bg: 'gray.100' }}
                                                          isLoading={handshakeLoading}
                                                          icon={<HandShakeSimpleIcon color="#F5AEAD" width="20" height="20" />}
                                                          w={'41px'}
                                                          height={'41px'}
                                                          border={'1px solid #00000033'}
                                                          borderRadius={'50%'}
                                                          variant="none"
                                                        />
                                                      </Tooltip>
                                                    ) : (
                                                      /* <Tooltip label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}> */
                                                      <Tooltip zIndex="99999999" label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}>
                                                        <IconButton
                                                          w={'41px'}
                                                          height={'41px'}
                                                          border={'1px solid #00000033'}
                                                          borderRadius={'50%'}
                                                          variant="none"
                                                          _hover={{ bg: 'gray.100' }}
                                                          onClick={() => handleHandshake(user)}
                                                          isLoading={loadingButtonId === user.id}
                                                          icon={<HandShakeSimpleIcon color="#037F29" width="20" height="20" />}
                                                        />
                                                      </Tooltip>
                                                    )}
                                                  </HStack>
                                                ) : (
                                                  <Box
                                                    w={'41px'}
                                                    height={'41px'}
                                                    border={'1px solid #00000033'}
                                                    cursor="pointer"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    borderRadius={'50%'}
                                                    onClick={() => {
                                                      const roomId = getRoomIdFromLS(user?.id);
                                                      router.push(`/profile/message?user=${roomId}`);
                                                    }}
                                                    _hover={{ bg: 'gray.100' }}
                                                  >
                                                    <ChatOutLined/>
                                                  </Box>
                                                )
                                              )}
                                            </ForceSignInWrapper>
                                          </HStack>
                                        </Flex>
                                      ))}
                                    </VStack>
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </>
                      ) : (
                        ''
                      )}
                    </HStack>
                  </MyTestimonialsUserCard>
                 </>)}
                )}
            </Stack>
            {testimonials?.length > 2 ? (
              <ShowAllLink href="/profile/testimonials" />
            ) : (
              ''
            )}
          </>
        ) : (
          <></>
        )}
      </Stack>
    </>
  );
});
